/** localStorage key for the browser-to-identity clock offset, so a reload keeps it until boot re-checks it. */
const CLOCK_OFFSET_KEY = 'ct-platform-clock-offset';

/**
 * The longest a request may take for its answer to still say what time it is
 * on the server. A laptop that sleeps between the request and the answer would
 * otherwise read the whole nap as clock skew.
 */
const MAX_CLOCK_SAMPLE_DELAY_MS = 30_000;

/** The offset persisted by an earlier page, or 0 when there is none. */
function readClockOffset(): number {
  try {
    const stored = Number(localStorage.getItem(CLOCK_OFFSET_KEY));
    return Number.isFinite(stored) ? stored : 0;
  } catch {
    return 0;
  }
}

/**
 * The server's clock minus this browser's, in milliseconds. Token freshness is
 * judged against the server's clock rather than the browser's: a laptop clock a
 * few minutes slow would otherwise send tokens the gateway already calls
 * expired, and one running fast would renew on every request.
 */
let clockOffsetMs = readClockOffset();

/** The current time on the server's clock, as best this browser knows it. */
export function serverNow(): number {
  return Date.now() + clockOffsetMs;
}

/** Records one sample, unless the request behind it took too long to tell the time. */
function remember(serverTimeMs: number, requestStartedAt: number): void {
  if (Date.now() - requestStartedAt > MAX_CLOCK_SAMPLE_DELAY_MS) return;
  clockOffsetMs = serverTimeMs - Date.now();
  try {
    localStorage.setItem(CLOCK_OFFSET_KEY, String(clockOffsetMs));
  } catch {
    /** Storage can be unavailable; the in-memory offset still serves this page. */
  }
}

/**
 * Learns the server's clock from a token identity has just issued: its `iat` is
 * identity's "now" at the moment of issue. Call it only with a token minted for
 * the request that started at `requestStartedAt`.
 */
export function rememberServerClock(
  freshToken: string,
  requestStartedAt: number,
): void {
  try {
    const segment = freshToken.split('.')[1] ?? '';
    const issuedAt = (
      JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/'))) as {
        iat?: unknown;
      }
    ).iat;
    if (typeof issuedAt === 'number')
      remember(issuedAt * 1000, requestStartedAt);
  } catch {
    /** An undecodable token teaches nothing about the clock. */
  }
}

/**
 * Learns the server's clock from a response's `Date` header. Boot does this
 * with `/v1/config`, so an offset kept from an earlier page is checked again
 * before it is trusted: a browser clock corrected since then would otherwise
 * make an expired token look fresh. The header has one-second resolution,
 * plenty for a two-minute margin.
 */
export function rememberServerDate(
  dateHeader: string | null,
  requestStartedAt: number,
): void {
  const serverTimeMs = dateHeader ? Date.parse(dateHeader) : NaN;
  if (Number.isFinite(serverTimeMs)) remember(serverTimeMs, requestStartedAt);
}

/** Test-only: forgets the learned offset. */
export function _resetServerClockForTests(): void {
  clockOffsetMs = 0;
}
